/**
 * Adapts the flat `PublicRosterRow[]` a public share-view payload carries
 * into the shape CreatorsTab's columns read.
 *
 * The public payload (`packages/features/src/campaign-detail/api/
 * shareGrants.ts`) is FLAT: `username`, `name?`, `avatar_url?`, `status`,
 * `followers?`, `avg_views?`, `engagement_rate?`, `deliverables?`,
 * `live_url?` — and has no `id`. CreatorsTab's columns (CreatorsTab.tsx)
 * expect the AUTHED, NESTED roster shape: `c.platform.username` /
 * `c.platform.name` / `c.platform.profile_picture_url`,
 * `c.snapshot_metrics.followers` / `avg_views` / `engagement_rate`, plus
 * `c.id`.
 *
 * `id` is synthesized from `username` — unique per platform per campaign
 * (the roster domain never has two rows for the same username on the same
 * campaign/platform). The decoy `PublicRosterTable`
 * (`publicShellViews.tsx`) already relies on this exact assumption for its
 * own `getRowId={(row) => row.username}`.
 *
 * `estimated_budget` / `estimated_cpv` pass through the row's OWN
 * negotiation-time figures (2026-08-05 parity decision — row-level money is
 * no longer masked; only table-wide AGGREGATES, which this endpoint never
 * queries, stay hidden). Default to `0` only when the wire value is
 * genuinely absent (a still-negotiating row with nothing committed yet),
 * matching every other optional numeric field this adapter maps.
 *
 * `id`/`user_id`/`platform`, `snapshot_metrics`, and `deliverables` are
 * mapped via `../publicRosterRowSharedFields.ts` — the overlap Task 17's
 * review found this adapter shares near-verbatim with Confirmed's sibling
 * (`../ConfirmedTab.publicRosterRowAdapter.ts`), extracted in Task 18 so a
 * third (and now fourth/fifth, Content/Live-Content) adapter doesn't re-copy
 * it. Only the fields genuinely specific to this tab are inlined below.
 */
import type { PublicRosterRow } from '@discovr/features';
import type { CreatorsRow } from './types';
import {
  adaptPublicRosterRowIdentity,
  adaptPublicRosterRowSnapshotMetrics,
  adaptPublicRosterRowDeliverables,
} from '../publicRosterRowSharedFields';

/** Adapted row shape, structurally close to `RosterEntryCreators` but with
 *  every admin-only / write-flow field defaulted to an inert placeholder —
 *  nothing in public mode ever reads them (every action button is gated by
 *  the all-read-only public `permissions` object, and the public shell's
 *  click-interceptor cancels any click that could reach one anyway). */
export interface PublicCreatorsRow {
  id: string;
  user_id: string;
  status: string;
  status_updated_at: string;
  platform: {
    username: string;
    name: string;
    profile_picture_url: string;
    is_verified: boolean;
  };
  snapshot_metrics: {
    followers: number;
    avg_views: number;
    engagement_rate: number;
  };
  deliverables: Array<{ type: string; base_price?: number; is_selected: boolean }>;
  estimated_cpv: number | null;
  estimated_reach: number;
  estimated_budget: number;
  selection_thread_locked: boolean;
  unread_count: number;
  posts: never[];
  addons: never[];
  timestamps: { created_at: string; updated_at: string };
}

function adaptPublicRosterRow(row: PublicRosterRow): PublicCreatorsRow {
  return {
    ...adaptPublicRosterRowIdentity(row),
    status: row.status,
    status_updated_at: '',
    ...adaptPublicRosterRowSnapshotMetrics(row),
    ...adaptPublicRosterRowDeliverables(row),
    // null (not 0) when the public payload carries no per-row CPV. EstimatedCPVCell
    // renders "—" for both null and a real 0 (never "₹0.00"), but null vs 0 stays
    // meaningful upstream, so a genuine 0 (if ever present) is preserved as-is.
    estimated_cpv: row.estimated_cpv ?? null,
    estimated_reach: 0,
    estimated_budget: row.estimated_budget ?? 0,
    selection_thread_locked: true,
    unread_count: 0,
    posts: [],
    addons: [],
    timestamps: { created_at: '', updated_at: '' },
  };
}

export function adaptPublicRosterRows(rows: PublicRosterRow[]): PublicCreatorsRow[] {
  return rows.map(adaptPublicRosterRow);
}

/** Cast helper for the one call site (CreatorsTab.tsx) that must feed these
 *  rows into `DataTable<CreatorsRow>`. Kept as a named export (rather than an
 *  inline `as unknown as` at the call site) so the type gap is documented in
 *  exactly one place. */
export function adaptPublicRosterRowsAsCreatorsRows(rows: PublicRosterRow[]): CreatorsRow[] {
  return adaptPublicRosterRows(rows) as unknown as CreatorsRow[];
}
